import React, { useEffect, useState } from 'react';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { Page, HeaderCard, FormCard, Section, Button, TabNav, Tab } from './FoodPremisesStyles';
import { Oval } from 'react-loader-spinner';
import { FaDownload, FaCheckCircle, FaTimesCircle, FaClock, FaSignOutAlt } from 'react-icons/fa';
import FoodPremisesApplicationForm from '../components/FoodPremisesApplicationForm';

const BusinessDashboard = () => {
  const { user, logout } = useAuth();
  const [app, setApp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('status');

  useEffect(() => {
    fetchApplication();
  }, []);

  const fetchApplication = async () => {
    try {
      const data = await api.foodPremises.getMyApplication();
      setApp(data);
      if (data) setActiveTab('status');
      else setActiveTab('application');
    } catch (err) {
      setApp(null);
      setActiveTab('application');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'approved': return '#16a34a';
      case 'rejected': return '#dc2626';
      default: return '#ca8a04';
    }
  };

  const getStatusIcon = (status) => {
    switch(status) {
      case 'approved': return <FaCheckCircle />;
      case 'rejected': return <FaTimesCircle />;
      default: return <FaClock />;
    }
  };

  const handleLogout = async () => {
    await logout();
    window.location.href = '/';
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center' }}><Oval color="#000" height={40} width={40} /></div>;

  return (
    <Page>
      <Section>
        <HeaderCard style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h1>Business Dashboard</h1>
            <p>Welcome, {user?.username} {app ? `(${app.businessOwnerName})` : ''}</p>
          </div>
          <Button onClick={handleLogout} style={{ background: '#dc2626', fontSize: '14px', padding: '10px 16px', display: 'flex', alignItems: 'center', gap: 6 }}>
            <FaSignOutAlt /> Logout
          </Button>
        </HeaderCard>
        
        <TabNav>
          <Tab $active={activeTab === 'status'} onClick={() => setActiveTab('status')}>Application Status</Tab>
          <Tab $active={activeTab === 'application'} onClick={() => setActiveTab('application')}>{app ? 'My Application Details' : 'Application Form'}</Tab>
        </TabNav>
      </Section>

      <Section>
        {activeTab === 'status' && (
          <FormCard>
            {app ? (
              <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                <div style={{ fontSize: 64, color: getStatusColor(app.status), marginBottom: 24 }}>
                  {getStatusIcon(app.status)}
                </div>
                <h2 style={{ fontSize: 32, color: getStatusColor(app.status), marginBottom: 16, textTransform: 'capitalize' }}>
                  {app.status === 'pending' ? 'Application Pending' : `Application ${app.status}`}
                </h2>
                
                {app.status === 'pending' && (
                  <p style={{ fontSize: 18, color: '#666', maxWidth: 600, margin: '0 auto' }}>
                    Your application is currently being reviewed by our team. Please check back later for updates.
                  </p>
                )}

                {app.status === 'rejected' && (
                  <p style={{ fontSize: 18, color: '#dc2626', maxWidth: 600, margin: '0 auto' }}>
                    Unfortunately, your application has been rejected. Please contact support or resubmit your application.
                  </p>
                )}

                {app.status === 'approved' && (
                  <div style={{ marginTop: 32 }}>
                     <p style={{ fontSize: 18, color: '#16a34a', marginBottom: 24 }}>
                      Congratulations! Your application has been approved and your certificate is ready.
                    </p>
                    {app.certificate && (
                      <a href={app.certificate} download="FAL_Certificate.png" style={{ textDecoration: 'none' }}>
                        <Button style={{ display: 'inline-flex', alignItems: 'center', gap: 12, padding: '16px 32px', fontSize: 18 }}>
                          <FaDownload /> Download Certificate
                        </Button>
                      </a>
                    )}
                  </div>
                )}
                
                <div style={{ marginTop: 40, color: '#999', fontSize: 14 }}>
                  Application submitted on {new Date(app.createdAt).toLocaleDateString()}
                </div>
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                <div style={{ fontSize: 48, color: '#9ca3af', marginBottom: 16 }}>
                  <FaClock />
                </div>
                <h2>No Application Submitted</h2>
                <p style={{ color: '#666', marginBottom: 24 }}>You have not submitted a food premises application yet.</p>
                <Button onClick={() => setActiveTab('application')}>Go to Application Form</Button>
              </div>
            )}
          </FormCard>
        )}

        {activeTab === 'application' && (
          <>
            {app ? (
              <FormCard>
                <h3 style={{ borderBottom: '2px solid #eee', paddingBottom: 8, marginBottom: 16 }}>Premises Details</h3>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                  <div><strong>Premises Name:</strong> {app.premisesName}</div>
                  <div><strong>Physical Address:</strong> {app.physicalAddress}</div>
                  <div><strong>Contact:</strong> {app.contactNumbers}</div>
                  <div><strong>Email:</strong> {app.email}</div>
                  <div><strong>County:</strong> {app.county}</div>
                  <div><strong>District:</strong> {app.district}</div>
                  <div><strong>Type:</strong> {app.typeOfPremises?.join(', ')}</div>
                  <div><strong>Product Categories:</strong> {app.productCategories?.join(', ')} {app.otherProductCategory ? `(${app.otherProductCategory})` : ''}</div>
                  <div><strong>Registration No:</strong> {app.businessRegistrationNumber}</div>
                  <div><strong>Employees:</strong> {app.numberOfEmployees}</div>
                  <div><strong>FAL Code:</strong> {app.falPremisesCode || 'N/A'}</div>
                  <div><strong>Date of Listing:</strong> {app.dateOfListing || 'N/A'}</div>
                </div>
              </FormCard>
            ) : (
              <FoodPremisesApplicationForm onSuccess={(data) => { setApp(data); setActiveTab('status'); }} user={user} />
            )}
          </>
        )}
      </Section>
    </Page>
  );
};

export default BusinessDashboard;
